import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { money } from "@/lib/totals";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Pencil, Trash2, Plus, Upload, Download, FileDown } from "lucide-react";

const empty = { barcode: "", item_alias: "", price: "", tax_rate: "5", stock: "100" };

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      barcode: p.barcode,
      item_alias: p.item_alias,
      price: String(p.price),
      tax_rate: String(p.tax_rate),
      stock: String(p.stock),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.barcode.trim() || !form.item_alias.trim())
      return toast.error("Barcode and name required");
    const body = {
      barcode: form.barcode.trim(),
      item_alias: form.item_alias.trim(),
      price: parseFloat(form.price) || 0,
      tax_rate: parseFloat(form.tax_rate) || 0,
      stock: parseInt(form.stock || "0", 10),
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, body);
        toast.success("Product updated");
      } else {
        await api.post("/products", body);
        toast.success("Product added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (p) => {
    await api.delete(`/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const downloadBlob = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadTemplate = async () => {
    const { data } = await api.get("/products/template", { responseType: "blob" });
    downloadBlob(data, "product_template.xlsx");
  };

  const exportCurrent = async () => {
    const { data } = await api.get("/products/export", { responseType: "blob" });
    downloadBlob(data, "products_export.xlsx");
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/import", fd);
      setImportResult(data);
      setImportOpen(true);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="pos-shell">
      <div className="pos-titlebar">
        <span>Product Management</span>
        <div className="right">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.csv"
            style={{ display: "none" }}
            onChange={onImportFile}
            data-testid="import-file-input"
          />
          <button className="title-btn" onClick={openNew} data-testid="add-product">
            <Plus size={13} style={{ display: "inline", marginRight: 4 }} />
            New Product
          </button>
          <button className="title-btn" onClick={() => fileRef.current?.click()} data-testid="import-xlsx">
            <Upload size={13} style={{ display: "inline", marginRight: 4 }} />
            Import XLSX
          </button>
          <button className="title-btn" onClick={downloadTemplate} data-testid="download-template">
            <Download size={13} style={{ display: "inline", marginRight: 4 }} />
            Template
          </button>
          <button className="title-btn" onClick={exportCurrent} data-testid="export-current">
            <FileDown size={13} style={{ display: "inline", marginRight: 4 }} />
            Export
          </button>
          <button className="title-btn" onClick={() => navigate("/")} data-testid="back-pos">
            <ArrowLeft size={13} style={{ display: "inline", marginRight: 4 }} />
            Back to POS
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: 12 }}>
        <div style={{ background: "#fff", border: "1px solid var(--pos-line)" }}>
          <table className="items-table">
            <thead>
              <tr>
                <th>Barcode / Item ID</th>
                <th>Item name alias</th>
                <th className="num">Price</th>
                <th className="num">Tax %</th>
                <th className="num">Stock</th>
                <th className="num">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} data-testid={`product-${p.id}`}>
                  <td>{p.barcode}</td>
                  <td>{p.item_alias}</td>
                  <td className="num">{money(p.price)}</td>
                  <td className="num">{p.tax_rate}</td>
                  <td className="num">{p.stock}</td>
                  <td className="num">
                    <button
                      className="title-btn"
                      style={{ marginRight: 6 }}
                      onClick={() => openEdit(p)}
                      data-testid={`edit-${p.id}`}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      className="title-btn"
                      onClick={() => remove(p)}
                      data-testid={`delete-${p.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">
              {editing ? "Edit Product" : "New Product"}
            </DialogTitle>
          </DialogHeader>
          <label className="login-label">Barcode / Item ID</label>
          <Input
            value={form.barcode}
            onChange={(e) => setForm({ ...form, barcode: e.target.value })}
            data-testid="product-barcode"
          />
          <label className="login-label" style={{ marginTop: 8 }}>
            Item name alias
          </label>
          <Input
            value={form.item_alias}
            onChange={(e) => setForm({ ...form, item_alias: e.target.value })}
            data-testid="product-alias"
          />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label className="login-label" style={{ marginTop: 8 }}>
                Price (incl. tax)
              </label>
              <Input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                data-testid="product-price"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="login-label" style={{ marginTop: 8 }}>
                Tax %
              </label>
              <Input
                type="number"
                value={form.tax_rate}
                onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                data-testid="product-tax"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="login-label" style={{ marginTop: 8 }}>
                Stock
              </label>
              <Input
                type="number"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                data-testid="product-stock"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} data-testid="product-save">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Import Result</DialogTitle>
          </DialogHeader>
          {importResult && (
            <div style={{ fontSize: 13 }}>
              <div style={{ display: "flex", gap: 16, marginBottom: 8 }}>
                <span data-testid="import-created">
                  <b>{importResult.created}</b> created
                </span>
                <span data-testid="import-updated">
                  <b>{importResult.updated}</b> updated
                </span>
                <span data-testid="import-skipped">
                  <b>{importResult.skipped.length}</b> skipped
                </span>
              </div>
              {importResult.skipped.length > 0 && (
                <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #eee" }}>
                  {importResult.skipped.map((s, i) => (
                    <div
                      key={i}
                      style={{ padding: "4px 8px", borderBottom: "1px solid #f0f0f0", fontSize: 12 }}
                    >
                      Row {s.row}
                      {s.barcode ? ` (${s.barcode})` : ""}: {s.reason}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportOpen(false)} data-testid="import-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
